import React, { useCallback, useEffect, useState } from "react";

import { getArticle, getMethodNames, getModuleNames, getTags, updateArticle } from "services";

import { incomplete, useCancelAsyncOnUnmount, useNavigate, useParams } from "utils";

import { AdminHead, Button, Col, ErrorBoundary, Form, MainPage, ModalContent, Row, Message as message } from "components";

import { ArticleInputs } from "pages/admin/content/components/ArticleInputs";

export const ArticleUpdate = () => {
  const [loading, setLoading] = useState(true);
  const [tags, setTags] = useState([]);
  const [methods, setMethods] = useState([]);
  const [modules, setModules] = useState([]);
  const [imageSuccess, setImageSuccess] = useState(false);
  const [imageURL, setImageURL] = useState(false);
  const [content, setContent] = useState("");
  const [article, setArticle] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const signal = useCancelAsyncOnUnmount()();
  const navigate = useNavigate();

  const { id } = useParams();

  const loadTags = useCallback(
    (language) => {
      getTags({
        language,
        signal,
      }).then((res) => {
        setTags(res);
      });
    },
    [signal]
  );

  const loadMethods = useCallback(() => {
    getMethodNames({
      signal,
    }).then((res) => {
      setMethods(res);
      loadTags();
    });
  }, [signal, loadTags]);

  const loadModules = useCallback(() => {
    getModuleNames({
      signal,
    }).then((res) => {
      setModules(res);
    });
  }, [signal]);

  const getCurrentArticle = useCallback(() => {
    setLoading(true);

    getArticle({
      id: id,
      signal,
    })
      .then((res) => {
        setArticle(res);
        setImageSuccess(res.image !== "");
        setImageURL(res.image !== "" ? res.image : false);
        setContent(res.content);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, signal]);

  const complete = (v) => {
    setLoading(true);
    updateArticle({
      ...v,
      link: article.link,
      image: imageURL,
      content: content,
      id: id,
      methods: v.methods && [v.methods],
      modules: v.modules && [v.modules],
      signal,
    })
      .then(() => {
        message({
          content: "The gremlins accepted your sacrifice and will consume it! Nom nom nom",
          icon: "gremlin",
        });
        navigate("/content/articles");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadMethods();
    loadModules();
    getCurrentArticle();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MainPage loading={loading} pageName={"admin"}>
      <ErrorBoundary>
        <AdminHead
          description={"Update the article content here. The URL will not be changed. Only the first 3 tags are displayed, but all tags are searched."}
          link={"/content/articles"}
          title={"Update an article"}
          tooletipText={"Back to articles"}
        />
      </ErrorBoundary>
      {!loading && (
        <Row className="midi-form">
          <Col>
            <Form name="confirm" onFinish={(v) => complete(v)} onFinishFailed={(v) => incomplete(v)}>
              <ErrorBoundary>
                <ArticleInputs
                  article={article}
                  content={content}
                  imageSuccess={imageSuccess}
                  imageURL={imageURL}
                  methods={methods}
                  modules={modules}
                  setContent={setContent}
                  setImageSuccess={setImageSuccess}
                  setImageURL={setImageURL}
                  setLoading={setLoading}
                  tags={tags}
                  reloadTags={loadTags}
                />
              </ErrorBoundary>
              <Row>
                <Col>
                  <Form.Item>
                    <Button type="primary" htmlType="submit" disabled={loading} loading={loading} className="margin-right-100">
                      Save
                    </Button>
                    <Button onClick={() => setShowModal(true)} disabled={loading} loading={loading}>
                      Preview content
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      )}
      <ModalContent visible={showModal} type="article" onCancel={() => setShowModal(false)}>
        <div className="content" dangerouslySetInnerHTML={{ __html: content }} />
      </ModalContent>
    </MainPage>
  );
};
